import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Save, Search, TestTube } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_FORM = {
  enabled: false,
  data_indexing_url: '',
  username: 'admin',
  password: '',
  verify_ssl: false,
  product_index_scope: 'account',
  resolved_index_name: '',
  openai_api_key: '',
  openai_base_url: '',
  embedding_model: 'text-embedding-3-small',
  azure_endpoint: '',
  azure_api_version: '2024-02-01',
  azure_deployment: 'text-embedding-3-small',
  default_top_k: 12,
  bulk_chunk_size: 25,
  bm25_weight: 0.4,
  vector_weight: 0.6,
};

function mapSettingsToForm(settings) {
  const src = settings || {};
  return {
    enabled: Boolean(src.enabled),
    data_indexing_url: String(src.data_indexing_url || src.base_url || '').trim(),
    username: String(src.username || DEFAULT_FORM.username).trim(),
    password: '',
    verify_ssl: Boolean(src.verify_ssl),
    product_index_scope:
      String(src.product_index_scope || '').trim().toLowerCase() === 'shared' ? 'shared' : 'account',
    resolved_index_name: String(src.resolved_index_name || '').trim(),
    openai_api_key: '',
    openai_base_url: String(src.openai_base_url || '').trim(),
    embedding_model: String(src.embedding_model || DEFAULT_FORM.embedding_model).trim(),
    azure_endpoint: String(src.azure_endpoint || '').trim(),
    azure_api_version: String(src.azure_api_version || DEFAULT_FORM.azure_api_version).trim(),
    azure_deployment: String(src.azure_deployment || DEFAULT_FORM.azure_deployment).trim(),
    default_top_k: Math.max(1, Math.min(50, Number(src.default_top_k) || DEFAULT_FORM.default_top_k)),
    bulk_chunk_size: Math.max(1, Math.min(500, Number(src.bulk_chunk_size) || DEFAULT_FORM.bulk_chunk_size)),
    bm25_weight: Number(src.bm25_weight ?? DEFAULT_FORM.bm25_weight),
    vector_weight: Number(src.vector_weight ?? DEFAULT_FORM.vector_weight),
  };
}

export default function AiSearchConfigPanel() {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [storedSettings, setStoredSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [openaiKeyConfigured, setOpenaiKeyConfigured] = useState(false);
  const [openaiKeyEditing, setOpenaiKeyEditing] = useState(false);
  const [osPasswordConfigured, setOsPasswordConfigured] = useState(false);
  const [osPasswordEditing, setOsPasswordEditing] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setStatusMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/settings`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Failed to load AI Search settings (${response.status})`);
      }
      const settings = data?.settings || {};
      setStoredSettings(settings);
      setForm(mapSettingsToForm(settings));
      setOpenaiKeyConfigured(Boolean(settings.has_openai_api_key));
      setOpenaiKeyEditing(false);
      setOsPasswordConfigured(Boolean(settings.has_password));
      setOsPasswordEditing(false);
    } catch (error) {
      setStatusMessage(String(error?.message || error));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildPayload = () => {
    const payload = {
      enabled: Boolean(form.enabled),
      data_indexing_url: String(form.data_indexing_url || '').trim(),
      base_url: String(form.data_indexing_url || '').trim(),
      username: String(form.username || DEFAULT_FORM.username).trim(),
      verify_ssl: Boolean(form.verify_ssl),
      product_index_scope: form.product_index_scope === 'shared' ? 'shared' : 'account',
      openai_base_url: String(form.openai_base_url || '').trim(),
      embedding_model: String(form.embedding_model || DEFAULT_FORM.embedding_model).trim(),
      azure_endpoint: String(form.azure_endpoint || '').trim(),
      azure_api_version: String(form.azure_api_version || DEFAULT_FORM.azure_api_version).trim(),
      azure_deployment: String(form.azure_deployment || DEFAULT_FORM.azure_deployment).trim(),
      default_top_k: Math.max(1, Math.min(50, Number(form.default_top_k) || DEFAULT_FORM.default_top_k)),
      bulk_chunk_size: Math.max(1, Math.min(500, Number(form.bulk_chunk_size) || DEFAULT_FORM.bulk_chunk_size)),
      bm25_weight: Number(form.bm25_weight ?? DEFAULT_FORM.bm25_weight),
      vector_weight: Number(form.vector_weight ?? DEFAULT_FORM.vector_weight),
    };
    if (openaiKeyEditing || form.openai_api_key.trim()) {
      payload.openai_api_key = form.openai_api_key.trim();
    }
    if (osPasswordEditing || form.password.trim()) {
      payload.password = form.password.trim();
    }
    return payload;
  };

  const saveSettings = async () => {
    const bm25 = Number(form.bm25_weight || 0);
    const vector = Number(form.vector_weight || 0);
    if (Math.abs(bm25 + vector - 1) > 0.0001) {
      setStatusMessage('BM25 and Vector weights must add up to 1.0');
      return;
    }
    setSaving(true);
    setStatusMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Failed to save AI Search settings (${response.status})`);
      }
      const settings = data?.settings || {};
      setStoredSettings(settings);
      setForm(mapSettingsToForm(settings));
      setOpenaiKeyConfigured(Boolean(settings.has_openai_api_key));
      setOpenaiKeyEditing(false);
      setOsPasswordConfigured(Boolean(settings.has_password));
      setOsPasswordEditing(false);
      setStatusMessage('AI Search settings saved successfully.');
    } catch (error) {
      setStatusMessage(String(error?.message || error));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    const bm25 = Number(form.bm25_weight || 0);
    const vector = Number(form.vector_weight || 0);
    if (Math.abs(bm25 + vector - 1) > 0.0001) {
      setStatusMessage('BM25 and Vector weights must add up to 1.0 before testing');
      return;
    }
    setTesting(true);
    setConnectionTestResult(null);
    setStatusMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/test-connection`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Connection test failed (${response.status})`);
      }
      setConnectionTestResult(data);
      if (data?.resolved_index_name) {
        setForm((prev) => ({ ...prev, resolved_index_name: String(data.resolved_index_name) }));
      }
      setStatusMessage(data?.ok ? 'Connection test passed.' : 'Connection test failed.');
    } catch (error) {
      setConnectionTestResult({
        ok: false,
        opensearch: { ok: false, error: String(error?.message || error) },
        embedding: { ok: false, error: 'Test not completed' },
      });
      setStatusMessage(String(error?.message || error));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      {statusMessage && (
        <div
          className={`alert ${statusMessage.toLowerCase().includes('success') || statusMessage.toLowerCase().includes('passed') ? 'alert-success' : 'alert-warning'}`}
          role="status"
        >
          {statusMessage}
        </div>
      )}

      <div className="card border-0 bg-light mb-4">
        <div className="card-header bg-transparent">
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <Search size={20} className="text-primary" />
            AI Search
          </h6>
        </div>
        <div className="card-body">
          <div className="form-check form-switch mb-4">
            <input
              className="form-check-input"
              type="checkbox"
              id="aiSearchEnabled"
              checked={Boolean(form.enabled)}
              onChange={(e) => updateField('enabled', e.target.checked)}
            />
            <label className="form-check-label" htmlFor="aiSearchEnabled">
              Enable AI Search
            </label>
            <div className="form-text">
              When disabled, hybrid AI search requests are rejected for this account.
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label" htmlFor="dataIndexingUrl">
              Data Indexing URL
            </label>
            <input
              id="dataIndexingUrl"
              type="url"
              className="form-control"
              value={form.data_indexing_url}
              onChange={(e) => updateField('data_indexing_url', e.target.value)}
              placeholder="https://dev-osearch.extremeb2b.com:8443"
            />
            <div className="form-text">
              OpenSearch cluster host and port only — not the AI Tech Assistant app URL (avoid paths like
              /accounts or /api).
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label">Product collection</label>
            <div className="d-flex flex-column flex-md-row gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="productIndexScope"
                  id="productIndexScopeAccount"
                  checked={form.product_index_scope !== 'shared'}
                  onChange={() => updateField('product_index_scope', 'account')}
                />
                <label className="form-check-label" htmlFor="productIndexScopeAccount">
                  Account collection (<code>products_&lt;account id&gt;</code>)
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="productIndexScope"
                  id="productIndexScopeShared"
                  checked={form.product_index_scope === 'shared'}
                  onChange={() => updateField('product_index_scope', 'shared')}
                />
                <label className="form-check-label" htmlFor="productIndexScopeShared">
                  Shared collection (<code>products</code>)
                </label>
              </div>
            </div>
            {form.resolved_index_name ? (
              <div className="form-text mt-1">
                Active index for this account: <code>{form.resolved_index_name}</code>
              </div>
            ) : null}
          </div>

          <div className="mb-4">
            <label className="form-label" htmlFor="bulkChunkSize">
              Docs per <code>_bulk</code> request
            </label>
            <input
              id="bulkChunkSize"
              type="number"
              min={1}
              max={500}
              className="form-control"
              style={{ maxWidth: 160 }}
              value={form.bulk_chunk_size}
              onChange={(e) => updateField('bulk_chunk_size', Number(e.target.value) || DEFAULT_FORM.bulk_chunk_size)}
            />
            <div className="form-text">
              Product indexing sends this many documents per OpenSearch <code>_bulk</code> call (1–500). Use a
              smaller value if nginx returns 413; the job still splits batches in half on 413 until they succeed.
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12 col-md-4">
              <label className="form-label" htmlFor="osUsername">
                OpenSearch Username
              </label>
              <input
                id="osUsername"
                type="text"
                className="form-control"
                value={form.username}
                onChange={(e) => updateField('username', e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">OpenSearch Password</label>
              {!osPasswordConfigured || osPasswordEditing ? (
                <input
                  type="password"
                  className="form-control"
                  value={form.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder="••••••••"
                />
              ) : (
                <div className="input-group">
                  <input type="password" className="form-control" value="configured" readOnly />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setOsPasswordEditing(true);
                      updateField('password', '');
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <div className="col-12 col-md-4 d-flex align-items-end">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="osVerifySsl"
                  checked={Boolean(form.verify_ssl)}
                  onChange={(e) => updateField('verify_ssl', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="osVerifySsl">
                  Verify SSL certificate
                </label>
              </div>
            </div>
          </div>

          <div className="border rounded-3 p-3 p-md-4 bg-white">
            <h6 className="mb-1">Embeddings</h6>
            <p className="text-muted small mb-3">
              Configure OpenAI or Azure OpenAI credentials and hybrid search weighting.
            </p>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">OpenAI API Key (embeddings)</label>
                <div className="input-group">
                  {!openaiKeyConfigured || openaiKeyEditing ? (
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      className="form-control"
                      value={form.openai_api_key}
                      onChange={(e) => updateField('openai_api_key', e.target.value)}
                      placeholder="sk-..."
                    />
                  ) : (
                    <input type="password" className="form-control" value="configured" readOnly />
                  )}
                  {(!openaiKeyConfigured || openaiKeyEditing) ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowApiKey((v) => !v)}
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setOpenaiKeyEditing(true);
                        updateField('openai_api_key', '');
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">OpenAI Base URL (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.openai_base_url}
                  onChange={(e) => updateField('openai_base_url', e.target.value)}
                  placeholder="https://api.openai.com/v1"
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Embedding Model</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.embedding_model}
                  onChange={(e) => updateField('embedding_model', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Azure Endpoint (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.azure_endpoint}
                  onChange={(e) => updateField('azure_endpoint', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Azure API Version</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.azure_api_version}
                  onChange={(e) => updateField('azure_api_version', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Azure Deployment</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.azure_deployment}
                  onChange={(e) => updateField('azure_deployment', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Default Top K</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  className="form-control"
                  value={form.default_top_k}
                  onChange={(e) =>
                    updateField(
                      'default_top_k',
                      Math.max(1, Math.min(50, Number(e.target.value || DEFAULT_FORM.default_top_k)))
                    )
                  }
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">BM25 Weight</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={1}
                  className="form-control"
                  value={form.bm25_weight}
                  onChange={(e) => updateField('bm25_weight', Number(e.target.value || 0))}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Vector Weight</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={1}
                  className="form-control"
                  value={form.vector_weight}
                  onChange={(e) => updateField('vector_weight', Number(e.target.value || 0))}
                />
              </div>
            </div>
          </div>

          {connectionTestResult && (
            <div className={`alert mt-3 mb-0 ${connectionTestResult.ok ? 'alert-success' : 'alert-danger'}`}>
              <div className="fw-semibold mb-1">
                {connectionTestResult.ok ? 'Connection test successful' : 'Connection test failed'}
              </div>
              <div className="small">
                Index: {connectionTestResult?.opensearch?.ok ? 'OK' : `Error — ${connectionTestResult?.opensearch?.error || 'unknown'}`}
                {connectionTestResult?.resolved_index_name ? (
                  <span>
                    {' '}
                    (<code>{connectionTestResult.resolved_index_name}</code>)
                  </span>
                ) : null}
              </div>
              <div className="small">
                Embeddings:{' '}
                {connectionTestResult?.embedding?.ok
                  ? `OK (${connectionTestResult?.embedding?.provider || 'provider'} / ${connectionTestResult?.embedding?.dimensions || 0} dims)`
                  : `Error — ${connectionTestResult?.embedding?.error || 'unknown'}`}
              </div>
            </div>
          )}

          <div className="d-flex flex-wrap gap-2 mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={loadSettings} disabled={loading}>
              {loading ? 'Loading...' : 'Reload'}
            </button>
            <button type="button" className="btn btn-outline-primary" onClick={testConnection} disabled={testing || loading}>
              <TestTube size={18} className="me-1" />
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={saving || loading}>
              <Save size={18} className="me-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
